import { useState, useEffect } from 'react';

/**
 * useDebounce Hook
 * 
 * Delays value updates by specified milliseconds.
 * Useful for debouncing search inputs, resize handlers, and other frequent updates.
 * 
 * @template T - Type of the value being debounced
 * @param {T} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {T} Debounced value
 * @example
 * const searchTerm = useDebounce(inputValue, 300);
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}